import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { deliverApi, orderApi } from '../services/api'
import Loading from '../components/Loading'
import ErrorMessage from '../components/ErrorMessage'
import StatusBadge from '../components/StatusBadge'
import { channelMap } from '../utils/helpers'
import type { Order } from '../types'

export default function Deliver() {
  const navigate = useNavigate()
  const [orderNo, setOrderNo] = useState('')
  const [trackingNo, setTrackingNo] = useState('')
  const [carrier, setCarrier] = useState('')
  const [order, setOrder] = useState<Order | null>(null)
  const [step, setStep] = useState<'scan' | 'ship' | 'success'>('scan')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const carriers = ['顺丰速运', '中通快递', '圆通快递', '申通快递', '韵达快递', '京东物流', '德邦快递']

  const scanOrder = async () => {
    if (!orderNo.trim()) {
      setError('请扫描或输入订单号')
      return
    }
    setLoading(true)
    setError('')
    try {
      const data = await orderApi.get(orderNo.trim())
      setOrder(data as Order)
      setStep('ship')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '查询失败'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const submitOutbound = async () => {
    if (!order || !trackingNo.trim() || !carrier) {
      setError('请填写完整的快递信息')
      return
    }
    setLoading(true)
    setError('')
    try {
      await deliverApi.scanOutbound(order.orderNo, {
        trackingNo: trackingNo.trim(),
        carrier,
      })
      setStep('success')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '提交失败'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  if (step === 'scan') {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#eeeeee' }}>
        <div className="nav-bar">
          <button onClick={() => navigate('/')} style={{ color: '#9a9a9a', background: 'none', border: 'none', cursor: 'pointer' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 16, fontWeight: 500, color: '#313131', letterSpacing: '0.15em' }}>
            发货扫描
          </h1>
          <div style={{ width: 20 }} />
        </div>

        <div style={{ padding: '24px' }}>
          <div className="card">
            <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 12, letterSpacing: '0.15em' }}>
              扫描订单号
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={orderNo}
                onChange={(e) => setOrderNo(e.target.value)}
                placeholder="扫描或输入订单号"
                className="input"
                style={{ flex: 1 }}
                onKeyDown={(e) => e.key === 'Enter' && scanOrder()}
              />
              <button onClick={scanOrder} disabled={loading} className="btn-primary" style={{ padding: '14px 16px' }}>
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
              </button>
            </div>
            <p style={{ fontSize: 13, color: '#9a9a9a', marginTop: 12, letterSpacing: '0.15em' }}>
              扫描成品包裹上的订单标签条码
            </p>
          </div>

          {error && <ErrorMessage message={error} onRetry={() => setError('')} />}
          {loading && <Loading />}
        </div>
      </div>
    )
  }

  if (step === 'ship' && order) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#eeeeee' }}>
        <div className="nav-bar">
          <button onClick={() => setStep('scan')} style={{ color: '#9a9a9a', background: 'none', border: 'none', cursor: 'pointer' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 16, fontWeight: 500, color: '#313131', letterSpacing: '0.15em' }}>
            扫描出库
          </h1>
          <div style={{ width: 20 }} />
        </div>

        <div style={{ padding: '24px' }}>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>{channelMap[order.channel]}</span>
              <StatusBadge status={order.status} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 500, color: '#313131', letterSpacing: '0.15em' }}>{order.orderNo}</div>
          </div>

          <div className="card">
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 12, letterSpacing: '0.15em' }}>
                扫描快递单号
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={trackingNo}
                  onChange={(e) => setTrackingNo(e.target.value)}
                  placeholder="扫描或输入快递单号"
                  className="input"
                  style={{ flex: 1 }}
                  onKeyDown={(e) => e.key === 'Enter' && submitOutbound()}
                />
                <button className="btn-primary" style={{ padding: '14px 16px' }}>
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                </button>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 12, letterSpacing: '0.15em' }}>
                快递公司
              </label>
              <select value={carrier} onChange={(e) => setCarrier(e.target.value)} className="select">
                <option value="">请选择快递公司</option>
                {carriers.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          {error && <ErrorMessage message={error} onRetry={() => setError('')} />}

          <button onClick={submitOutbound} disabled={!trackingNo.trim() || !carrier || loading} className="btn-primary" style={{ width: '100%', marginTop: 24 }}>
            {loading ? '提交中' : '确认出库'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#eeeeee', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 24px' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 64,
          height: 64,
          border: '1px solid #313131',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
        }}>
          <svg width="28" height="28" fill="none" stroke="#313131" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 22, fontWeight: 500, color: '#313131', letterSpacing: '0.15em', marginBottom: 8 }}>
          出库成功
        </h2>
        <p style={{ fontSize: 15, color: '#9a9a9a', letterSpacing: '0.15em', marginBottom: 4 }}>快递单号：{trackingNo}</p>
        <p style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em', marginBottom: 32 }}>客户将收到发货通知</p>
        <button
          onClick={() => { setStep('scan'); setOrderNo(''); setTrackingNo(''); setCarrier('') }}
          className="btn-primary"
        >
          继续扫描
        </button>
      </div>
    </div>
  )
}
